// Handbuch: zeigt docs/radiocast/HANDBUCH.md (liegt als web/src/handbuch.md bei der Oberfläche).
// Kapitel = Abschnitte "## Titel {#kennung}"; die Links „Im Handbuch“ der Seiten führen per Kennung hierher.

import { Fragment, ReactNode } from "react";
import text from "../handbuch.md?raw";
import { PageHead } from "../ui";

type Chapter = { id: string; title: string; body: string[] };

function chapters(): Chapter[] {
  const out: Chapter[] = [];
  let cur: Chapter | null = null;
  for (const line of text.split("\n")) {
    const m = /^## (.+?)\s*\{#([a-z0-9-]+)\}\s*$/.exec(line);
    if (m) {
      cur = { id: m[2], title: m[1], body: [] };
      out.push(cur);
    } else if (cur) cur.body.push(line);
  }
  return out;
}
const CHAPTERS = chapters();
const INTRO = text.split("\n## ")[0].split("\n").filter((l) => !l.startsWith("# "));

// Older links of the pages lead to the matching chapter.
const ALIAS: Record<string, string> = {
  dashboard: "erste-schritte", plan: "uhren", planung: "uhren", kategorien: "bibliothek", sender: "streams",
  einstellungen: "streams", verwaltung: "benutzer", rotation: "bibliothek",
};

/** Inline: **bold**, `code`, [text](link). */
function inline(s: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let k = 0;
  for (const m of s.matchAll(re)) {
    if (m.index! > last) out.push(s.slice(last, m.index));
    const t = m[0];
    if (t.startsWith("**")) out.push(<b key={k++}>{t.slice(2, -2)}</b>);
    else if (t.startsWith("`")) out.push(<code key={k++}>{t.slice(1, -1)}</code>);
    else {
      const l = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(t)!;
      out.push(<a key={k++} href={l[2]}>{l[1]}</a>);
    }
    last = m.index! + t.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

/** Blocks: ### headings, lists (- and 1.), tables, > notes, ``` code, paragraphs. */
function Markdown({ lines }: { lines: string[] }) {
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const l = lines[i];
    if (!l.trim()) { i++; continue; }
    if (l.startsWith("```")) {
      const code: string[] = [];
      for (i++; i < lines.length && !lines[i].startsWith("```"); i++) code.push(lines[i]);
      i++;
      blocks.push(<pre key={k++} className="mono" style={{ background: "var(--paper)", padding: 10, borderRadius: 8, overflowX: "auto" }}>{code.join("\n")}</pre>);
    } else if (l.startsWith("### ")) {
      blocks.push(<h3 key={k++}>{inline(l.slice(4))}</h3>); i++;
    } else if (l.startsWith("|")) {
      const rows: string[][] = [];
      for (; i < lines.length && lines[i].startsWith("|"); i++) {
        if (/^\|[\s:|-]+\|$/.test(lines[i])) continue;
        rows.push(lines[i].slice(1, -1).split("|").map((c) => c.trim()));
      }
      blocks.push(
        <table key={k++} className="list" style={{ margin: "8px 0 14px" }}>
          <thead><tr>{rows[0].map((c, j) => <th key={j}>{inline(c)}</th>)}</tr></thead>
          <tbody>{rows.slice(1).map((r, n) => <tr key={n}>{r.map((c, j) => <td key={j}>{inline(c)}</td>)}</tr>)}</tbody>
        </table>,
      );
    } else if (/^\s*(-|\d+\.) /.test(l)) {
      const ordered = /^\s*\d+\. /.test(l);
      const items: { text: string; sub: string[] }[] = [];
      for (; i < lines.length && /^\s*(-|\d+\.) /.test(lines[i]); i++) {
        const indent = /^\s+/.test(lines[i]);
        const t = lines[i].replace(/^\s*(-|\d+\.) /, "");
        if (indent && items.length) items[items.length - 1].sub.push(t);
        else items.push({ text: t, sub: [] });
      }
      const List = ordered ? "ol" : "ul";
      blocks.push(
        <List key={k++} style={{ paddingLeft: 22, margin: "6px 0 12px" }}>
          {items.map((it, n) => <li key={n} style={{ margin: "3px 0" }}>{inline(it.text)}{it.sub.length > 0 && <ul style={{ paddingLeft: 18 }}>{it.sub.map((x, m) => <li key={m}>{inline(x)}</li>)}</ul>}</li>)}
        </List>,
      );
    } else if (l.startsWith("> ")) {
      blocks.push(<div key={k++} className="hint" style={{ margin: "8px 0" }}><span>ⓘ</span><span>{inline(l.slice(2))}</span></div>); i++;
    } else {
      const para: string[] = [];
      for (; i < lines.length && lines[i].trim() && !/^(#|\||>|```|\s*(-|\d+\.) )/.test(lines[i]); i++) para.push(lines[i]);
      blocks.push(<p key={k++} style={{ lineHeight: 1.55 }}>{inline(para.join(" "))}</p>);
    }
  }
  return <Fragment>{blocks}</Fragment>;
}

export function Manual({ chapter }: { chapter?: string }) {
  const id = ALIAS[chapter ?? ""] ?? chapter ?? CHAPTERS[0].id;
  const ch = CHAPTERS.find((c) => c.id === id) ?? CHAPTERS[0];
  return (
    <div className="manual">
      <PageHead global title="Handbuch" sub="Bedienung von Radiocast – Stand wie die Oberfläche" />
      <div style={{ display: "grid", gridTemplateColumns: "230px minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
        <nav className="card" style={{ padding: 6, position: "sticky", top: 0 }}>
          {CHAPTERS.map((c, n) => (
            <a key={c.id} href={`#/handbuch/${c.id}`} className={`lt-node${c.id === ch.id ? " cur" : ""}`} style={{ color: "var(--ink)", textDecoration: "none" }}>
              <span className="lt-n" style={{ marginLeft: 0, width: 20 }}>{n + 1}</span><span className="lt-name">{c.title}</span>
            </a>
          ))}
        </nav>
        <article className="card" style={{ maxWidth: 860 }}>
          {ch === CHAPTERS[0] && <Markdown lines={INTRO} />}
          <h2 style={{ marginTop: 0 }}>{ch.title}</h2>
          <Markdown lines={ch.body} />
        </article>
      </div>
    </div>
  );
}
